/**
 * Settings Module API
 *
 * Provides CRUD operations via GraphQL for:
 * - User Profile (usr-xxx)
 * - Team Members (usr-xxx)
 * - Appearance Settings
 * - Notification Preferences
 * - Integrations (email, calendar, storage)
 * - Agency Configuration
 */

import {
  createEnvelope,
  createErrorEnvelope,
  graphqlQuery,
  type Envelope,
  type ISODate,
} from '@business-assistant/shared';

// ============================================================================
// GRAPHQL HELPER (uses centralized client)
// ============================================================================

async function graphqlRequest<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  return graphqlQuery<T>(query, variables);
}

// ============================================================================
// TYPES
// ============================================================================

/**
 * User Profile
 */
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  jobTitle?: string;
  company?: string;
  phone?: string;
  timezone?: string;
  locale?: string;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/**
 * Team Member Role
 */
export type TeamMemberRole = 'owner' | 'admin' | 'member' | 'viewer';

/**
 * Module Permissions
 */
/**
 * Module Permission Level
 * "admin" | "modifica" | "visualizza" | "nessuno"
 */
export type PermissionLevel = 'admin' | 'modifica' | 'visualizza' | 'nessuno';

/**
 * Module Permissions (matches backend schema)
 */
export interface ModulePermissions {
  home: boolean;
  progetti: PermissionLevel;
  calendario: PermissionLevel;
  hub: PermissionLevel;
  contabilita: PermissionLevel;
  impostazioni: boolean;
}

/**
 * Team Member
 */
export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: TeamMemberRole;
  permissions: ModulePermissions;
  invitedAt: ISODate;
  joinedAt?: ISODate;
  isActive: boolean;
}

/**
 * Theme
 */
export type Theme = 'light' | 'dark' | 'auto';

/**
 * Layout Density
 */
export type LayoutDensity = 'compact' | 'comfortable';

/**
 * Sidebar Behavior
 */
export type SidebarBehavior = 'pinned' | 'overlay';

/**
 * Appearance Settings
 */
export interface AppearanceSettings {
  theme: Theme;
  layoutDensity: LayoutDensity;
  sidebarBehavior: SidebarBehavior;
}

/**
 * Notification Type
 */
export type NotificationType =
  | 'task_assigned'
  | 'due_date_upcoming'
  | 'new_message'
  | 'new_call'
  | 'invoice_due'
  | 'invoice_overdue';

/**
 * Notification Preferences
 */
export interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  notificationTypes: Record<NotificationType, boolean>;
}

/**
 * Integration Status
 */
export type IntegrationStatus = 'connected' | 'disconnected' | 'error';

/**
 * Email Integration
 */
export interface EmailIntegration {
  id: string;
  provider: 'gmail' | 'outlook';
  email: string;
  status: IntegrationStatus;
  syncEnabled: boolean;
  connectedAt?: ISODate;
  lastSyncAt?: ISODate;
}

/**
 * Calendar Integration
 */
export interface CalendarIntegration {
  id: string;
  provider: 'google' | 'outlook';
  email: string;
  status: IntegrationStatus;
  syncEnabled: boolean;
  connectedAt?: ISODate;
  lastSyncAt?: ISODate;
}

/**
 * Storage Integration
 */
export interface StorageIntegration {
  id: string;
  provider: 'google_drive' | 'dropbox' | 'onedrive';
  email: string;
  status: IntegrationStatus;
  connectedAt?: ISODate;
}

/**
 * Agency Size
 */
export type AgencySize = 'micro' | 'mini' | 'structured';

/**
 * Agency Configuration
 */
export interface AgencyConfiguration {
  size: AgencySize;
  companyName: string;
  vatNumber?: string;
  address?: string;
  features: {
    commissions: boolean;
    inventory: boolean;
    managementControl: boolean;
  };
  limits: {
    maxUsers: number;
    maxProjects: number;
    maxAccounts: number;
  };
}

/**
 * AI Settings
 */
export interface AISettings {
  enabled: boolean;
  useLocal: boolean;
  modules: {
    home: boolean;
    projects: boolean;
    calendar: boolean;
    hub: boolean;
    accounting: boolean;
  };
}

/**
 * Security Settings
 */
export interface SecuritySettings {
  mfaEnabled: boolean;
  sessionTimeout: number;
  requireStepUpAuth: boolean;
  auditLogRetention: number;
}

// ============================================================================
// GRAPHQL QUERIES
// ============================================================================

const QUERIES = {
  userProfile: `
    query UserProfile($userId: String) {
      userProfile(userId: $userId) {
        id
        name
        email
        avatarUrl
        jobTitle
        company
        phone
        timezone
        locale
        createdAt
        updatedAt
      }
    }
  `,
  teamMembers: `
    query TeamMembers {
      teamMembers {
        id
        name
        email
        role
        status
        permissions {
          home
          progetti
          calendario
          hub
          contabilita
          impostazioni
        }
        joinedAt
        lastAccessAt
        createdAt
        updatedAt
      }
    }
  `,
  appearanceSettings: `
    query AppearanceSettings($userId: String) {
      appearanceSettings(userId: $userId) {
        id
        theme
        layoutDensity
        sidebarBehavior
      }
    }
  `,
  agencyConfig: `
    query AgencyConfig($userId: String) {
      agencyConfig(userId: $userId) {
        id
        size
        companyName
        vatNumber
        address
        features {
          commissions
          inventory
          managementControl
        }
        limits {
          maxUsers
          maxProjects
          maxAccounts
        }
      }
    }
  `,
  securitySettings: `
    query SecuritySettings($userId: String) {
      securitySettings(userId: $userId) {
        id
        mfaEnabled
        sessionTimeout
        requireStepUpAuth
        auditLogRetention
      }
    }
  `,
  emailIntegrations: `
    query EmailIntegrations($userId: String) {
      emailIntegrations(userId: $userId) {
        id
        provider
        email
        status
        syncEnabled
        connectedAt
        lastSyncAt
      }
    }
  `,
  calendarIntegrations: `
    query CalendarIntegrations($userId: String) {
      calendarIntegrations(userId: $userId) {
        id
        provider
        email
        status
        syncEnabled
        connectedAt
        lastSyncAt
      }
    }
  `,
  storageIntegrations: `
    query StorageIntegrations($userId: String) {
      storageIntegrations(userId: $userId) {
        id
        provider
        email
        status
        connectedAt
      }
    }
  `,
};

const MUTATIONS = {
  saveUserProfile: `
    mutation SaveUserProfile($input: UserProfileInput!, $userId: String) {
      saveUserProfile(input: $input, userId: $userId) {
        id
        name
        email
        avatarUrl
        jobTitle
        company
        phone
        timezone
        locale
        createdAt
        updatedAt
      }
    }
  `,
  saveAppearanceSettings: `
    mutation SaveAppearanceSettings($input: AppearanceSettingsInput!, $userId: String) {
      saveAppearanceSettings(input: $input, userId: $userId) {
        id
        theme
        layoutDensity
        sidebarBehavior
      }
    }
  `,
  saveAgencyConfig: `
    mutation SaveAgencyConfig($input: AgencyConfigInput!, $userId: String) {
      saveAgencyConfig(input: $input, userId: $userId) {
        id
        size
        companyName
        vatNumber
        address
        features {
          commissions
          inventory
          managementControl
        }
        limits {
          maxUsers
          maxProjects
          maxAccounts
        }
      }
    }
  `,
  saveSecuritySettings: `
    mutation SaveSecuritySettings($input: SecuritySettingsInput!, $userId: String) {
      saveSecuritySettings(input: $input, userId: $userId) {
        id
        mfaEnabled
        sessionTimeout
        requireStepUpAuth
        auditLogRetention
      }
    }
  `,
  createTeamMember: `
    mutation CreateTeamMember($input: FullTeamMemberInput!) {
      createTeamMember(input: $input) {
        id
        name
        email
        role
        status
        permissions {
          home
          progetti
          calendario
          hub
          contabilita
          impostazioni
        }
        joinedAt
        createdAt
        updatedAt
      }
    }
  `,
  updateTeamMember: `
    mutation UpdateTeamMember($id: ID!, $input: FullTeamMemberInput!) {
      updateTeamMember(id: $id, input: $input) {
        id
        name
        email
        role
        status
        permissions {
          home
          progetti
          calendario
          hub
          contabilita
          impostazioni
        }
        joinedAt
        createdAt
        updatedAt
      }
    }
  `,
  deleteTeamMember: `
    mutation DeleteTeamMember($id: ID!) {
      deleteTeamMember(id: $id)
    }
  `,
  saveEmailIntegration: `
    mutation SaveEmailIntegration($input: EmailIntegrationInput!, $userId: String) {
      saveEmailIntegration(input: $input, userId: $userId) {
        id
        provider
        email
        status
        syncEnabled
        connectedAt
        lastSyncAt
      }
    }
  `,
  deleteEmailIntegration: `
    mutation DeleteEmailIntegration($id: ID!) {
      deleteEmailIntegration(id: $id)
    }
  `,
  saveCalendarIntegration: `
    mutation SaveCalendarIntegration($input: CalendarIntegrationInput!, $userId: String) {
      saveCalendarIntegration(input: $input, userId: $userId) {
        id
        provider
        email
        status
        syncEnabled
        connectedAt
        lastSyncAt
      }
    }
  `,
  deleteCalendarIntegration: `
    mutation DeleteCalendarIntegration($id: ID!) {
      deleteCalendarIntegration(id: $id)
    }
  `,
  saveStorageIntegration: `
    mutation SaveStorageIntegration($input: StorageIntegrationInput!, $userId: String) {
      saveStorageIntegration(input: $input, userId: $userId) {
        id
        provider
        email
        status
        connectedAt
      }
    }
  `,
  deleteStorageIntegration: `
    mutation DeleteStorageIntegration($id: ID!) {
      deleteStorageIntegration(id: $id)
    }
  `,
};

// ============================================================================
// PROFILE API
// ============================================================================

/**
 * Get current user profile
 */
export async function getUserProfile(): Promise<Envelope<UserProfile>> {
  try {
    const data = await graphqlRequest<{ userProfile: any }>(QUERIES.userProfile);
    if (data.userProfile) {
      return createEnvelope({
        id: data.userProfile.id,
        name: data.userProfile.name,
        email: data.userProfile.email,
        avatarUrl: data.userProfile.avatarUrl,
        jobTitle: data.userProfile.jobTitle,
        company: data.userProfile.company,
        phone: data.userProfile.phone,
        timezone: data.userProfile.timezone,
        locale: data.userProfile.locale,
        createdAt: data.userProfile.createdAt,
        updatedAt: data.userProfile.updatedAt,
      });
    }
    // Return default profile if none exists
    return createEnvelope({
      id: 'usr-default',
      name: 'Default User',
      email: 'user@example.com',
      timezone: 'Europe/Rome',
      locale: 'it-IT',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to load profile');
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  updates: Partial<UserProfile>
): Promise<Envelope<UserProfile>> {
  try {
    // Get current profile first
    const current = await getUserProfile();
    if (!current.success || !current.data) {
      return createErrorEnvelope('NOT_FOUND', 'Profile not found');
    }

    const input = {
      name: updates.name ?? current.data.name,
      email: updates.email ?? current.data.email,
      avatarUrl: updates.avatarUrl ?? current.data.avatarUrl ?? null,
      jobTitle: updates.jobTitle ?? current.data.jobTitle ?? null,
      company: updates.company ?? current.data.company ?? null,
      phone: updates.phone ?? current.data.phone ?? null,
      timezone: updates.timezone ?? current.data.timezone ?? null,
      locale: updates.locale ?? current.data.locale ?? null,
    };

    const data = await graphqlRequest<{ saveUserProfile: any }>(MUTATIONS.saveUserProfile, { input });
    if (data.saveUserProfile) {
      return createEnvelope({
        id: data.saveUserProfile.id,
        name: data.saveUserProfile.name,
        email: data.saveUserProfile.email,
        avatarUrl: data.saveUserProfile.avatarUrl,
        jobTitle: data.saveUserProfile.jobTitle,
        company: data.saveUserProfile.company,
        phone: data.saveUserProfile.phone,
        timezone: data.saveUserProfile.timezone,
        locale: data.saveUserProfile.locale,
        createdAt: data.saveUserProfile.createdAt,
        updatedAt: data.saveUserProfile.updatedAt,
      });
    }
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to update profile');
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to update profile');
  }
}

// ============================================================================
// TEAM API
// ============================================================================

/**
 * List team members
 */
export async function listTeamMembers(): Promise<Envelope<TeamMember[]>> {
  try {
    const data = await graphqlRequest<{ teamMembers: any[] }>(QUERIES.teamMembers);
    const members = (data.teamMembers || []).map((m: any) => ({
      id: m.id,
      name: m.name,
      email: m.email,
      role: mapRoleFromBackend(m.role),
      permissions: {
        home: m.permissions?.home ?? true,
        projects: m.permissions?.progetti ?? true,
        calendar: m.permissions?.calendario ?? true,
        hub: m.permissions?.hub ?? true,
        accounting: m.permissions?.contabilita ?? true,
        settings: m.permissions?.impostazioni ?? false,
      },
      invitedAt: m.createdAt,
      joinedAt: m.joinedAt,
      isActive: m.status === 'attivo',
    }));
    return createEnvelope(members);
  } catch (error) {
    return createEnvelope([]);
  }
}

function mapRoleFromBackend(role: string): TeamMemberRole {
  const roleMap: Record<string, TeamMemberRole> = {
    'admin': 'admin',
    'manager': 'admin',
    'membro': 'member',
    'ospite': 'viewer',
  };
  return roleMap[role] || 'member';
}

function mapRoleToBackend(role: TeamMemberRole): string {
  const roleMap: Record<TeamMemberRole, string> = {
    'owner': 'admin',
    'admin': 'admin',
    'member': 'membro',
    'viewer': 'ospite',
  };
  return roleMap[role] || 'membro';
}

/**
 * Invite team member
 */
export async function inviteTeamMember(data: {
  email: string;
  name: string;
  role: TeamMemberRole;
  permissions: ModulePermissions;
}): Promise<Envelope<TeamMember>> {
  try {
    const input = {
      name: data.name,
      email: data.email,
      avatarUrl: null,
      jobTitle: null,
      role: mapRoleToBackend(data.role),
      permissions: {
        home: data.permissions.home,
        oggi: true,
        progetti: data.permissions.projects,
        calendario: data.permissions.calendar,
        hub: data.permissions.hub,
        contabilita: data.permissions.accounting,
        impostazioni: data.permissions.settings,
      },
    };

    const result = await graphqlRequest<{ createTeamMember: any }>(MUTATIONS.createTeamMember, { input });
    if (result.createTeamMember) {
      const m = result.createTeamMember;
      return createEnvelope({
        id: m.id,
        name: m.name,
        email: m.email,
        role: mapRoleFromBackend(m.role),
        permissions: {
          home: m.permissions?.home ?? true,
          projects: m.permissions?.progetti ?? true,
          calendar: m.permissions?.calendario ?? true,
          hub: m.permissions?.hub ?? true,
          accounting: m.permissions?.contabilita ?? true,
          settings: m.permissions?.impostazioni ?? false,
        },
        invitedAt: m.createdAt,
        joinedAt: m.joinedAt,
        isActive: m.status === 'attivo',
      });
    }
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to invite team member');
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to invite team member');
  }
}

/**
 * Update team member
 */
export async function updateTeamMember(
  id: string,
  updates: Partial<Omit<TeamMember, 'id' | 'invitedAt'>>
): Promise<Envelope<void>> {
  try {
    // Get current member
    const members = await listTeamMembers();
    const current = members.data?.find(m => m.id === id);
    if (!current) {
      return createErrorEnvelope('NOT_FOUND', `Team member ${id} not found`);
    }

    const input = {
      name: updates.name ?? current.name,
      email: updates.email ?? current.email,
      avatarUrl: null,
      jobTitle: null,
      role: updates.role ? mapRoleToBackend(updates.role) : mapRoleToBackend(current.role),
      permissions: {
        home: updates.permissions?.home ?? current.permissions.home,
        oggi: true,
        progetti: updates.permissions?.projects ?? current.permissions.projects,
        calendario: updates.permissions?.calendar ?? current.permissions.calendar,
        hub: updates.permissions?.hub ?? current.permissions.hub,
        contabilita: updates.permissions?.accounting ?? current.permissions.accounting,
        impostazioni: updates.permissions?.settings ?? current.permissions.settings,
      },
    };

    await graphqlRequest(MUTATIONS.updateTeamMember, { id, input });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to update team member');
  }
}

/**
 * Remove team member
 */
export async function removeTeamMember(id: string): Promise<Envelope<void>> {
  try {
    await graphqlRequest(MUTATIONS.deleteTeamMember, { id });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to remove team member');
  }
}

/**
 * Get default permissions for role
 */
export function getDefaultPermissionsForRole(role: TeamMemberRole): ModulePermissions {
  switch (role) {
    case 'owner':
    case 'admin':
      return {
        home: true,
        projects: true,
        calendar: true,
        hub: true,
        accounting: true,
        settings: true,
      };
    case 'member':
      return {
        home: true,
        projects: true,
        calendar: true,
        hub: true,
        accounting: false,
        settings: false,
      };
    case 'viewer':
      return {
        home: true,
        projects: true,
        calendar: true,
        hub: false,
        accounting: false,
        settings: false,
      };
  }
}

// ============================================================================
// APPEARANCE API
// ============================================================================

/**
 * Get appearance settings
 */
export async function getAppearanceSettings(): Promise<Envelope<AppearanceSettings>> {
  try {
    const data = await graphqlRequest<{ appearanceSettings: any }>(QUERIES.appearanceSettings);
    if (data.appearanceSettings) {
      return createEnvelope({
        theme: data.appearanceSettings.theme as Theme,
        layoutDensity: data.appearanceSettings.layoutDensity as LayoutDensity,
        sidebarBehavior: data.appearanceSettings.sidebarBehavior as SidebarBehavior,
      });
    }
    // Return default settings
    return createEnvelope({
      theme: 'auto',
      layoutDensity: 'comfortable',
      sidebarBehavior: 'pinned',
    });
  } catch (error) {
    return createEnvelope({
      theme: 'auto',
      layoutDensity: 'comfortable',
      sidebarBehavior: 'pinned',
    });
  }
}

/**
 * Update appearance settings
 */
export async function updateAppearanceSettings(
  updates: Partial<AppearanceSettings>
): Promise<Envelope<void>> {
  try {
    const current = await getAppearanceSettings();
    const settings = current.data || { theme: 'auto', layoutDensity: 'comfortable', sidebarBehavior: 'pinned' };

    const input = {
      theme: updates.theme ?? settings.theme,
      layoutDensity: updates.layoutDensity ?? settings.layoutDensity,
      sidebarBehavior: updates.sidebarBehavior ?? settings.sidebarBehavior,
    };

    await graphqlRequest(MUTATIONS.saveAppearanceSettings, { input });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to update appearance settings');
  }
}

// ============================================================================
// NOTIFICATION API
// ============================================================================

// Notification preferences are stored locally for now (browser-specific)
let notificationPreferences: NotificationPreferences = {
  emailNotifications: true,
  pushNotifications: false,
  notificationTypes: {
    task_assigned: true,
    due_date_upcoming: true,
    new_message: true,
    new_call: true,
    invoice_due: true,
    invoice_overdue: true,
  },
};

/**
 * Get notification preferences
 */
export async function getNotificationPreferences(): Promise<Envelope<NotificationPreferences>> {
  return createEnvelope(notificationPreferences);
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  updates: Partial<NotificationPreferences>
): Promise<Envelope<void>> {
  notificationPreferences = {
    ...notificationPreferences,
    ...updates,
    notificationTypes: {
      ...notificationPreferences.notificationTypes,
      ...updates.notificationTypes,
    },
  };
  return createEnvelope(undefined);
}

// ============================================================================
// INTEGRATION API
// ============================================================================

/**
 * List email integrations
 */
export async function listEmailIntegrations(): Promise<Envelope<EmailIntegration[]>> {
  try {
    const data = await graphqlRequest<{ emailIntegrations: any[] }>(QUERIES.emailIntegrations);
    const integrations = (data.emailIntegrations || []).map((i: any) => ({
      id: i.id,
      provider: i.provider as 'gmail' | 'outlook',
      email: i.email,
      status: i.status as IntegrationStatus,
      syncEnabled: i.syncEnabled,
      connectedAt: i.connectedAt,
      lastSyncAt: i.lastSyncAt,
    }));
    return createEnvelope(integrations);
  } catch (error) {
    return createEnvelope([]);
  }
}

/**
 * Connect email integration
 */
export async function connectEmailIntegration(
  provider: 'gmail' | 'outlook',
  email: string
): Promise<Envelope<EmailIntegration>> {
  try {
    const input = {
      provider,
      email,
      status: 'connected',
      syncEnabled: true,
      connectedAt: new Date().toISOString(),
      lastSyncAt: null,
    };

    const data = await graphqlRequest<{ saveEmailIntegration: any }>(MUTATIONS.saveEmailIntegration, { input });
    if (data.saveEmailIntegration) {
      return createEnvelope({
        id: data.saveEmailIntegration.id,
        provider: data.saveEmailIntegration.provider,
        email: data.saveEmailIntegration.email,
        status: data.saveEmailIntegration.status,
        syncEnabled: data.saveEmailIntegration.syncEnabled,
        connectedAt: data.saveEmailIntegration.connectedAt,
        lastSyncAt: data.saveEmailIntegration.lastSyncAt,
      });
    }
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to connect email integration');
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to connect email integration');
  }
}

/**
 * Disconnect email integration
 */
export async function disconnectEmailIntegration(id: string): Promise<Envelope<void>> {
  try {
    await graphqlRequest(MUTATIONS.deleteEmailIntegration, { id });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to disconnect email integration');
  }
}

/**
 * List calendar integrations
 */
export async function listCalendarIntegrations(): Promise<Envelope<CalendarIntegration[]>> {
  try {
    const data = await graphqlRequest<{ calendarIntegrations: any[] }>(QUERIES.calendarIntegrations);
    const integrations = (data.calendarIntegrations || []).map((i: any) => ({
      id: i.id,
      provider: i.provider as 'google' | 'outlook',
      email: i.email,
      status: i.status as IntegrationStatus,
      syncEnabled: i.syncEnabled,
      connectedAt: i.connectedAt,
      lastSyncAt: i.lastSyncAt,
    }));
    return createEnvelope(integrations);
  } catch (error) {
    return createEnvelope([]);
  }
}

/**
 * Connect calendar integration
 */
export async function connectCalendarIntegration(
  provider: 'google' | 'outlook',
  email: string
): Promise<Envelope<CalendarIntegration>> {
  try {
    const input = {
      provider,
      email,
      status: 'connected',
      syncEnabled: true,
      connectedAt: new Date().toISOString(),
      lastSyncAt: null,
    };

    const data = await graphqlRequest<{ saveCalendarIntegration: any }>(MUTATIONS.saveCalendarIntegration, { input });
    if (data.saveCalendarIntegration) {
      return createEnvelope({
        id: data.saveCalendarIntegration.id,
        provider: data.saveCalendarIntegration.provider,
        email: data.saveCalendarIntegration.email,
        status: data.saveCalendarIntegration.status,
        syncEnabled: data.saveCalendarIntegration.syncEnabled,
        connectedAt: data.saveCalendarIntegration.connectedAt,
        lastSyncAt: data.saveCalendarIntegration.lastSyncAt,
      });
    }
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to connect calendar integration');
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to connect calendar integration');
  }
}

/**
 * Disconnect calendar integration
 */
export async function disconnectCalendarIntegration(id: string): Promise<Envelope<void>> {
  try {
    await graphqlRequest(MUTATIONS.deleteCalendarIntegration, { id });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to disconnect calendar integration');
  }
}

/**
 * List storage integrations
 */
export async function listStorageIntegrations(): Promise<Envelope<StorageIntegration[]>> {
  try {
    const data = await graphqlRequest<{ storageIntegrations: any[] }>(QUERIES.storageIntegrations);
    const integrations = (data.storageIntegrations || []).map((i: any) => ({
      id: i.id,
      provider: i.provider as 'google_drive' | 'dropbox' | 'onedrive',
      email: i.email,
      status: i.status as IntegrationStatus,
      connectedAt: i.connectedAt,
    }));
    return createEnvelope(integrations);
  } catch (error) {
    return createEnvelope([]);
  }
}

/**
 * Connect storage integration
 */
export async function connectStorageIntegration(
  provider: 'google_drive' | 'dropbox' | 'onedrive',
  email: string
): Promise<Envelope<StorageIntegration>> {
  try {
    const input = {
      provider,
      email,
      status: 'connected',
      connectedAt: new Date().toISOString(),
    };

    const data = await graphqlRequest<{ saveStorageIntegration: any }>(MUTATIONS.saveStorageIntegration, { input });
    if (data.saveStorageIntegration) {
      return createEnvelope({
        id: data.saveStorageIntegration.id,
        provider: data.saveStorageIntegration.provider,
        email: data.saveStorageIntegration.email,
        status: data.saveStorageIntegration.status,
        connectedAt: data.saveStorageIntegration.connectedAt,
      });
    }
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to connect storage integration');
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to connect storage integration');
  }
}

/**
 * Disconnect storage integration
 */
export async function disconnectStorageIntegration(id: string): Promise<Envelope<void>> {
  try {
    await graphqlRequest(MUTATIONS.deleteStorageIntegration, { id });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to disconnect storage integration');
  }
}

// ============================================================================
// AGENCY API
// ============================================================================

/**
 * Get agency configuration
 */
export async function getAgencyConfiguration(): Promise<Envelope<AgencyConfiguration>> {
  try {
    const data = await graphqlRequest<{ agencyConfig: any }>(QUERIES.agencyConfig);
    if (data.agencyConfig) {
      return createEnvelope({
        size: data.agencyConfig.size as AgencySize,
        companyName: data.agencyConfig.companyName,
        vatNumber: data.agencyConfig.vatNumber,
        address: data.agencyConfig.address,
        features: {
          commissions: data.agencyConfig.features?.commissions ?? false,
          inventory: data.agencyConfig.features?.inventory ?? false,
          managementControl: data.agencyConfig.features?.managementControl ?? false,
        },
        limits: {
          maxUsers: data.agencyConfig.limits?.maxUsers ?? 1,
          maxProjects: data.agencyConfig.limits?.maxProjects ?? 10,
          maxAccounts: data.agencyConfig.limits?.maxAccounts ?? 5,
        },
      });
    }
    // Return default config
    return createEnvelope({
      size: 'micro',
      companyName: 'My Agency',
      features: {
        commissions: false,
        inventory: false,
        managementControl: false,
      },
      limits: {
        maxUsers: 1,
        maxProjects: 10,
        maxAccounts: 5,
      },
    });
  } catch (error) {
    return createEnvelope({
      size: 'micro',
      companyName: 'My Agency',
      features: {
        commissions: false,
        inventory: false,
        managementControl: false,
      },
      limits: {
        maxUsers: 1,
        maxProjects: 10,
        maxAccounts: 5,
      },
    });
  }
}

/**
 * Update agency configuration
 */
export async function updateAgencyConfiguration(
  updates: Partial<AgencyConfiguration>
): Promise<Envelope<void>> {
  try {
    const current = await getAgencyConfiguration();
    const config = current.data!;

    const input = {
      size: updates.size ?? config.size,
      companyName: updates.companyName ?? config.companyName,
      vatNumber: updates.vatNumber ?? config.vatNumber ?? null,
      address: updates.address ?? config.address ?? null,
      features: {
        commissions: updates.features?.commissions ?? config.features.commissions,
        inventory: updates.features?.inventory ?? config.features.inventory,
        managementControl: updates.features?.managementControl ?? config.features.managementControl,
      },
      limits: {
        maxUsers: updates.limits?.maxUsers ?? config.limits.maxUsers,
        maxProjects: updates.limits?.maxProjects ?? config.limits.maxProjects,
        maxAccounts: updates.limits?.maxAccounts ?? config.limits.maxAccounts,
      },
    };

    await graphqlRequest(MUTATIONS.saveAgencyConfig, { input });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to update agency configuration');
  }
}

// ============================================================================
// AI API
// ============================================================================

// AI settings kept simple for now
let aiSettings: AISettings = {
  enabled: true,
  useLocal: false,
  modules: {
    home: true,
    projects: true,
    calendar: true,
    hub: true,
    accounting: true,
  },
};

/**
 * Get AI settings
 */
export async function getAISettings(): Promise<Envelope<AISettings>> {
  return createEnvelope(aiSettings);
}

/**
 * Update AI settings
 */
export async function updateAISettings(
  updates: Partial<AISettings>
): Promise<Envelope<void>> {
  aiSettings = {
    ...aiSettings,
    ...updates,
    modules: {
      ...aiSettings.modules,
      ...updates.modules,
    },
  };
  return createEnvelope(undefined);
}

// ============================================================================
// SECURITY API
// ============================================================================

/**
 * Get security settings
 */
export async function getSecuritySettings(): Promise<Envelope<SecuritySettings>> {
  try {
    const data = await graphqlRequest<{ securitySettings: any }>(QUERIES.securitySettings);
    if (data.securitySettings) {
      return createEnvelope({
        mfaEnabled: data.securitySettings.mfaEnabled,
        sessionTimeout: data.securitySettings.sessionTimeout,
        requireStepUpAuth: data.securitySettings.requireStepUpAuth,
        auditLogRetention: data.securitySettings.auditLogRetention,
      });
    }
    // Return default settings
    return createEnvelope({
      mfaEnabled: false,
      sessionTimeout: 60,
      requireStepUpAuth: true,
      auditLogRetention: 90,
    });
  } catch (error) {
    return createEnvelope({
      mfaEnabled: false,
      sessionTimeout: 60,
      requireStepUpAuth: true,
      auditLogRetention: 90,
    });
  }
}

/**
 * Update security settings
 */
export async function updateSecuritySettings(
  updates: Partial<SecuritySettings>
): Promise<Envelope<void>> {
  try {
    const current = await getSecuritySettings();
    const settings = current.data!;

    const input = {
      mfaEnabled: updates.mfaEnabled ?? settings.mfaEnabled,
      sessionTimeout: updates.sessionTimeout ?? settings.sessionTimeout,
      requireStepUpAuth: updates.requireStepUpAuth ?? settings.requireStepUpAuth,
      auditLogRetention: updates.auditLogRetention ?? settings.auditLogRetention,
    };

    await graphqlRequest(MUTATIONS.saveSecuritySettings, { input });
    return createEnvelope(undefined);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to update security settings');
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Clear all data (for testing/reset)
 */
export function clearAllData(): void {
  notificationPreferences = {
    emailNotifications: true,
    pushNotifications: false,
    notificationTypes: {
      task_assigned: true,
      due_date_upcoming: true,
      new_message: true,
      new_call: true,
      invoice_due: true,
      invoice_overdue: true,
    },
  };
  aiSettings = {
    enabled: true,
    useLocal: false,
    modules: {
      home: true,
      projects: true,
      calendar: true,
      hub: true,
      accounting: true,
    },
  };
}

/**
 * Seed sample data - NO-OP, data comes from GraphQL
 */
export async function seedSampleData(): Promise<void> {
  console.log('[Settings API] seedSampleData is deprecated. Data is loaded from GraphQL server.');
}
